/**
 * TestingGroupedView — the TESTING segment as a hierarchy: each live path heads
 * a section with its landers/offers beneath it. Every row sits on ONE shared
 * column template (see testing-grid), so identity, age, money and the next-run
 * verdict line up down the whole page. One button collapses/expands every path
 * section at once; each still toggles on its own chevron.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import type {
  DemoteDestination,
  QueueElement,
  QueuePoolSummary,
  RotationElementDiff,
} from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import type { BulkAwareProps } from '../../bulk/row/bulk-row.types';
import { type RotationSwap } from '../../rotation/rotation-swaps';
import { TestingGridHeader } from './testing-grid';
import TestingNextRunSummary, { nextRunTotals } from './TestingNextRunSummary';
import { groupTesting } from './group-testing';
import TestingPathSection from './TestingPathSection';

export interface TestingGroupedViewProps extends BulkAwareProps {
  campaignId: string;
  provider: QueueProvider;
  /** Move a live PATH off the tracker; surfaced on each path's header row. */
  onDemote: (element: QueueElement, to: DemoteDestination) => void;
  demotePending: boolean;
  elements: QueueElement[];
  pools: QueuePoolSummary[];
  /** bankId → the planned swap taking this element OUT next run. */
  atRisk: Map<string, RotationSwap>;
  /** bankId → the planned diff HOLDING this element (gate / pool floor). */
  blocked: Map<string, RotationElementDiff>;
}

const TestingGroupedView: FC<TestingGroupedViewProps> = ({
  elements,
  pools,
  atRisk,
  blocked,
  campaignId,
  provider,
  onDemote,
  demotePending,
  bulk,
}) => {
  const groups = useMemo(() => groupTesting(elements), [elements]);
  const pathKeys = useMemo(
    () => groups.flatMap((g) => (g.path ? [g.path.bankId] : [])),
    [groups],
  );
  // Sections default open; a bankId in `collapsed` is folded. One button drives
  // them all, individual chevrons still work — both write the same set.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const allCollapsed =
    pathKeys.length > 0 && pathKeys.every((k) => collapsed.has(k));
  const toggleAll = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(pathKeys));
  const toggleOne = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const CollapseIcon = allCollapsed ? ChevronsUpDown : ChevronsDownUp;
  const ctx = {
    pools,
    atRisk,
    blocked,
    meta: { campaignId, provider, onDemote, demotePending, bulk },
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <TestingNextRunSummary totals={nextRunTotals(elements, atRisk, blocked)} />
        {pathKeys.length > 0 ? (
          <button
            type="button"
            onClick={toggleAll}
            className="mr-1 inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CollapseIcon className="h-3.5 w-3.5" aria-hidden />
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        ) : null}
      </div>
      <TestingGridHeader />
      {groups.map((group, i) => {
        const key = group.path ? group.path.bankId : `orphans-${i}`;
        return (
          <TestingPathSection
            key={key}
            group={group}
            open={group.path ? !collapsed.has(key) : true}
            onToggle={() => group.path && toggleOne(key)}
            ctx={ctx}
          />
        );
      })}
    </div>
  );
};

export default TestingGroupedView;
